
import { useState, useEffect, useCallback } from 'react';
import { supabase, type Profile } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const ensureProfile = useCallback(
    async (session: Session) => {
      const currentUser = session.user;

      // Check if profile exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching existing profile:', fetchError);
      }

      if (existingProfile) {
        setProfile(existingProfile);
        setLoading(false);
        return;
      }

      // Create profile for new social login users
      const meta = currentUser.user_metadata || {};
      const username =
        meta.full_name ||
        meta.user_name ||
        meta.name ||
        meta.preferred_username ||
        currentUser.email?.split('@')[0] ||
        `hacker_${Math.floor(Math.random() * 99999)}`;

      const avatarUrl = meta.avatar_url || meta.picture || null;

      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: currentUser.id,
          username: username.replace(/\s+/g, '_').toLowerCase(),
          display_name: meta.full_name || meta.name || username,
          avatar_url: avatarUrl,
          points: 0,
          reputation: 0,
        })
        .select()
        .maybeSingle();

      if (insertError) {
        console.error('Error creating profile:', insertError);
        // If insert fails (e.g. duplicate), try loading again
        await loadProfile(currentUser.id);
      } else {
        setProfile(newProfile);
        setLoading(false);
      }
    },
    [loadProfile]
  );

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error('Session error:', error);
          if (mounted) setLoading(false);
          return;
        }

        if (session?.user && mounted) {
          setUser(session.user);
          await ensureProfile(session);
        } else if (mounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Init auth error:', err);
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('Auth event:', event);

      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        await ensureProfile(session);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        setUser(session.user);
        await loadProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [ensureProfile, loadProfile]);

  const signIn = async (provider: 'google' | 'github') => {
    try {
      const currentOrigin = window.location.origin;
      const basePath = (globalThis as any).__BASE_PATH__ || '';
      const redirectUrl = `${currentOrigin}${basePath}/`;

      console.log('OAuth redirect URL:', redirectUrl);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return {
    user,
    profile,
    loading,
    signIn,
    signOut,
  };
}
